import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import { getFirestore, doc, getDoc, getDocFromServer, setDoc, updateDoc, collection, getDocs, deleteDoc, query, where, orderBy, limit, onSnapshot, runTransaction, connectFirestoreEmulator } from "firebase/firestore";
import {
  getAuth, onAuthStateChanged, signOut, connectAuthEmulator, signInWithEmailAndPassword,
  sendPasswordResetEmail, getMultiFactorResolver, multiFactor, TotpMultiFactorGenerator,
} from "firebase/auth";
import { getDatabase, ref as dbRef, onValue, off, connectDatabaseEmulator } from "firebase/database";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject, connectStorageEmulator } from "firebase/storage";

const firebaseConfig = {
  apiKey: "AIzaSyDfbk2Iya6b7ieDeOyXHHL8RO52ojA3PdQ",
  authDomain: "nyc-suite.firebaseapp.com",
  projectId: "nyc-suite",
  storageBucket: "nyc-suite.firebasestorage.app",
  messagingSenderId: "1030681987581",
  appId: "1:1030681987581:web:074cbb36a4613a5bc97194",
  // RTDB URL set explicitly so RTDB-region selection is deterministic.
  databaseURL: "https://nyc-suite-default-rtdb.firebaseio.com",
};

const APPCHECK_SITE_KEY = "6LfXkeUsAAAAAFe_K79v2Ai7BPite_7BEeEZhAEA";
const APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN;

const app = initializeApp(firebaseConfig);

// Dev-only: route the SDK at the local emulators (firebase emulators:start).
// Enabled by VITE_USE_EMULATORS=true. Never set in a production build, so prod
// always talks to live nyc-suite.
const USE_EMULATORS = import.meta.env.VITE_USE_EMULATORS === "true";

if (!import.meta.env.PROD && APPCHECK_DEBUG_TOKEN && typeof self !== "undefined") {
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = APPCHECK_DEBUG_TOKEN === "true"
    ? true
    : APPCHECK_DEBUG_TOKEN;
}

// App Check init is wrapped because a synchronous throw here (e.g.
// reCAPTCHA CDN blocked, domain not whitelisted, network failure) would
// break module load, prevent React from mounting, and leave the user on
// a blank page. Backend enforcement is off (see feedback_app_check_disabled
// memory), so failing open here is safe.
// App Check would try to load reCAPTCHA against localhost under the emulator —
// skip it there (callable enforcement is off regardless).
// 2026-08-04: client init disabled by default. The ReCaptcha token exchange
// started failing on the nycsuite.io origin (api2/clr 503s) and Auth 11.x
// attaches App Check tokens, which took down sign-in (auth/missing-project-id)
// and the quote AI callable. Enforcement is off server-side, so the client
// token buys nothing today. Re-enable with VITE_ENABLE_APPCHECK=1 after the
// site key/domain issue is fixed — required before the client portal ships.
const ENABLE_APPCHECK = import.meta.env.VITE_ENABLE_APPCHECK === "1";
if (!USE_EMULATORS && ENABLE_APPCHECK) {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(APPCHECK_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (e) {
    console.error("[AppCheck] initializeAppCheck failed; continuing without it:", e);
  }
}

export const db = getFirestore(app);
export const auth = getAuth(app);
export const rtdb = getDatabase(app);
export const functions = getFunctions(app, "us-central1");
export const storage = getStorage(app);

if (USE_EMULATORS) {
  connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "localhost", 8080);
  connectDatabaseEmulator(rtdb, "localhost", 9000);
  connectFunctionsEmulator(functions, "localhost", 5001);
  connectStorageEmulator(storage, "localhost", 9199);
  if (typeof window !== "undefined") {
    // Dev affordance so a test harness can sign in without the sign-in screen.
    window.__fb = {
      app, db, auth, functions, storage, rtdb,
      signIn: (email, password) => signInWithEmailAndPassword(auth, email, password),
    };
    console.info("[firebase] using LOCAL EMULATORS");
  }
}

// options passes through to httpsCallable — notably { timeout: ms }: the SDK's
// default is 70s, which silently undercuts any function with a longer
// timeoutSeconds (claudeProxy allows 300s server-side; the client must match or
// long extractions die with deadline-exceeded while the server is still working).
export const callable = (name, options) => httpsCallable(functions, name, options);

export {
  doc, getDoc, getDocFromServer, setDoc, updateDoc, collection, getDocs, deleteDoc, query, where, orderBy, limit, onSnapshot, runTransaction,
  onAuthStateChanged, signOut,
  // Password sign-in and the authenticator code (src/lib/authFlow.js).
  signInWithEmailAndPassword, sendPasswordResetEmail, getMultiFactorResolver, multiFactor, TotpMultiFactorGenerator,
  dbRef, onValue, off,
  storageRef, uploadBytes, getDownloadURL, deleteObject,
};
